/**
 * PR-E2 — GLSL source for the SDF text shader pair.
 *
 * The shaders implement Chlumsky's multi-channel SDF (msdf) technique:
 * three signed distance fields packed into the R/G/B channels of the
 * atlas texture. The fragment shader takes the *median* of the three
 * channels per fragment, which preserves sharp corners that would
 * otherwise round off in a single-channel SDF.
 *
 * # Vertex shader
 *
 * One INSTANCED quad per glyph. The geometry on the GPU is a
 * 4-vertex × 6-index unit quad (`THREE.PlaneGeometry(1, 1)` baked at
 * (0,0)-(1,1) — see `textRunRenderer.ts` for the buffer build). Per-
 * instance attributes:
 *
 *   - `instance_offset` (vec2): glyph quad lower-left corner in text-
 *     local 2D coordinates (X = horizontal cursor advance, Y = baseline-
 *     relative). The renderer uses world-units that match the text
 *     entity's `height` field.
 *   - `instance_scale`  (vec2): glyph quad width × height in the same
 *     text-local units. Multiplied with the unit-quad position to size
 *     the quad.
 *   - `instance_color`  (vec3): RGB color of this glyph (per-run/per-
 *     entity color from `MTextRun.colorHex` or the resolved entity
 *     color for single-line `Text`).
 *   - `instance_uv_min` (vec2): atlas-UV of the glyph's lower-left.
 *   - `instance_uv_max` (vec2): atlas-UV of the glyph's upper-right.
 *   - `instance_height` (float): glyph height in **screen px** at the
 *     current zoom — fed to the fragment shader so it can scale the
 *     SDF distance range accurately. (Computed CPU-side in the
 *     adapter; PR-E5 will add zoom-driven recalculation.)
 *
 * The `position` attribute is the unit-quad corner (0..1 on each axis).
 * Vertex output:
 *
 *   - `v_uv`     — interpolated atlas UV (lerped between
 *     instance_uv_min and instance_uv_max via the unit-quad position).
 *   - `v_color`  — flat color from `instance_color`.
 *   - `v_screenPxRange` — distance-range scale, also forwarded.
 *
 * # Fragment shader
 *
 * Multi-channel SDF resolution:
 *
 *   1. Sample atlas at `v_uv`, take the RGB triple as three independent
 *      signed distance fields.
 *   2. Compute `median(r, g, b)` — the value that two of three channels
 *      agree on. This is the crisp-corner property of msdf.
 *   3. Convert to alpha via `clamp((sd - 0.5) * screenPxRange + 0.5)`.
 *      The 0.5 baseline is the SDF zero-distance contour; the slope is
 *      `screenPxRange`, which controls anti-aliasing edge width.
 *   4. Output `vec4(v_color, alpha)`.
 *
 * `screenPxRange` formula (set per-instance via `instance_height`):
 *
 *     screenPxRange = max(distanceRange * (instance_height / atlasSize), 1.0)
 *
 * The `max(..., 1.0)` floor keeps edges from going subpixel-thin at
 * extreme zoom-out — they fade to translucent rather than aliasing.
 *
 * # CLAUDE.md ПРИОРИТЕТ #1 — full fidelity
 *
 * The msdf median() is the AutoCAD-equivalent crisp-corner technique
 * (msdfgen is the same toolchain VG/Blender use). No quadratic-only
 * fallback or single-channel SDF — both lose corner sharpness.
 *
 * `texture2D` is used for WebGL1 compatibility; the WebGPU path
 * (PR-E5+) gets a `textureSample` variant.
 */

/**
 * Vertex shader source.
 *
 * Three.js's `ShaderMaterial` injects `projectionMatrix`,
 * `modelViewMatrix`, `position`, `uv` automatically — we don't redeclare
 * them. The instanced attributes are declared by the application via
 * `InstancedBufferAttribute` and bound by name.
 *
 * The unit-quad's `position.xy` is in [0,1]; we transform it into
 * text-local space by multiplying with `instance_scale` then adding
 * `instance_offset`. That gives a 2D point, which we lift to z=0 before
 * the model-view-projection multiply.
 */
export const TEXT_VERTEX_SHADER = /* glsl */ `
  attribute vec2 instance_offset;
  attribute vec2 instance_scale;
  attribute vec3 instance_color;
  attribute vec2 instance_uv_min;
  attribute vec2 instance_uv_max;
  attribute float instance_height;
  // PR-E3: oblique (slant) angle in radians applied as an X-shear
  // proportional to the local Y position. Zero leaves the glyph
  // upright. Positive values lean the glyph's top-right (matches the
  // DXF \\Q convention: positive degrees = right-leaning italic).
  attribute float instance_oblique;
  // PR-E3: decoration flag — 0 = glyph quad (sample atlas as usual),
  // 1 = solid decoration line (underline / overline / strike). The
  // fragment shader uses this to skip the atlas lookup and emit the
  // run's color at full opacity.
  attribute float instance_decoration;

  uniform float distanceRange;
  uniform float atlasSize;

  varying vec2 v_uv;
  varying vec3 v_color;
  varying float v_screenPxRange;
  varying float v_decoration;

  void main() {
    // Unit-quad corner in [0,1]; lerp the atlas UV between the
    // glyph's bottom-left and top-right entries.
    vec2 unitCorner = position.xy;
    vec2 atlasUv = mix(instance_uv_min, instance_uv_max, unitCorner);
    v_uv = atlasUv;
    v_color = instance_color;
    v_decoration = instance_decoration;

    // screenPxRange: how many fragments the SDF distance range
    // (px @ atlas resolution) covers in screen space at this glyph's
    // current rendered height. The 1.0 floor keeps anti-aliasing edge
    // width from collapsing on extreme zoom-out.
    v_screenPxRange = max(distanceRange * (instance_height / atlasSize), 1.0);

    // Place the corner at instance_offset + instance_scale * unitCorner,
    // then apply oblique shear (X += Y * tan(oblique)). Tan is computed
    // CPU-side would be cheaper for static instance_oblique but doing
    // it here keeps the shader self-contained — the uniform path lands
    // in PR-E5 once we batch by run instead of per-glyph.
    vec2 textLocal = instance_offset + instance_scale * unitCorner;
    float shear = tan(instance_oblique);
    textLocal.x += textLocal.y * shear;

    vec4 mvPosition = modelViewMatrix * vec4(textLocal, 0.0, 1.0);
    gl_Position = projectionMatrix * mvPosition;
  }
`;

/**
 * Fragment shader source. See file header for the msdf median()
 * derivation and screenPxRange formula.
 *
 * The `median` helper is open-coded as a min/max combination — that's
 * the standard branchless 3-element median; trying to use a built-in
 * `median()` would be non-portable across WebGL drivers.
 */
export const TEXT_FRAGMENT_SHADER = /* glsl */ `
  precision highp float;

  uniform sampler2D atlas;

  varying vec2 v_uv;
  varying vec3 v_color;
  varying float v_screenPxRange;
  varying float v_decoration;

  /**
   * Branchless median of three floats. The crisp-corner property of
   * multi-channel SDF text comes from picking the value two of the
   * three channels agree on — equivalent to the median.
   */
  float median(float r, float g, float b) {
    return max(min(r, g), min(max(r, g), b));
  }

  void main() {
    // PR-E3: decoration quads (underline / overline / strike) skip
    // the atlas lookup and render as solid run-coloured rectangles.
    // We compare against 0.5 so float interpolation noise around the
    // 0/1 boundary doesn't trip the wrong branch.
    if (v_decoration > 0.5) {
      gl_FragColor = vec4(v_color, 1.0);
      return;
    }
    vec3 msd = texture2D(atlas, v_uv).rgb;
    float sd = median(msd.r, msd.g, msd.b);
    float alpha = clamp((sd - 0.5) * v_screenPxRange + 0.5, 0.0, 1.0);
    if (alpha <= 0.0) discard;
    gl_FragColor = vec4(v_color, alpha);
  }
`;

/** Stable identifier used by tests / the auditor to assert linkage. */
export const TEXT_SHADER_VERSION = "PR-E3";
